import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Form,
  Field,
  useWatch,
  WatchField,
  useForm,
  isEmpty
} from '../../../../form'
import { Translation, useTranslation } from 'react-i18next'
import { withStyles } from '@material-ui/core/styles'
import { getInvoiceUserEmail, getUserCompanyUpdateInfo } from '../../../../actions'
import Button from '@material-ui/core/Button'
import IconTooltip from '../../../../utils/IconTooltip'
import { getInvoicesList } from '../../../../selectors/invoice.selector'
import mobileView from '../../../../utils/mobile.utils'
import classNames from 'classnames'

const styles = (theme) => ({
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  checkbox: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#808080',
    marginBottom: '10px'
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonBack: {
    padding: !mobileView ? '2px 30px' : '4px 20px',
    color: 'rgb(83 77 77)',
    backgroundColor: '#fff',
    border: '2px solid #44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#E0F7FA'
    }
  },
  buttonNext: {
    padding: !mobileView ? '0 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  }
})

const InvoiceAdditionalInfoForm = ({ classes, goBack, goNext, formValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const invoices = useSelector(getInvoicesList)

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    keepEmptyValuesOnSubmit: false
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    formState: { errors }
  } = form

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    goNext(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  const watch = useWatch({
    fields: [
      'company_name',
      'business_id',
      'total_sum',
      'groupTotalAmount',
      'groupInvoiceRow',
      'quick_pay',
      'groupInvoice',
      'vat_selection',
      'customer_type',
      'rows'
    ],
    control
  })
  const company_name = watch.company_name
  const business_id = watch.business_id
  const groupTotalAmount = parseFloat(watch.groupTotalAmount)
  const groupInvoice = watch.groupInvoice

  const current_date = new Date()
  let quick_pay_overdue_by_customer = []
  if (company_name !== '' && business_id !== '') {
    quick_pay_overdue_by_customer = invoices.filter(
      (inv) =>
        inv.instant_payment === 'quick_pay' &&
        (inv.status === 3 || inv.status === 5) &&
        inv.remindersent === 1 &&
        current_date > new Date(inv.due_date) &&
        inv.business_id === business_id
    )
  }
  let disable_quick_pay
  if (quick_pay_overdue_by_customer.length >= 1) {
    disable_quick_pay = true
  } else {
    disable_quick_pay = false
  }

  let isGroupInvoiceEnabled = groupInvoice ? true : false

  const isGroupInvoice = isGroupInvoiceEnabled || groupTotalAmount > 0

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
  }, [])

  return (
    <Form {...form} style={{
      display: 'flex',
      height: '100%',
      flexDirection: 'column'
    }}>
      <div className="row" style={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center', 
        padding: mobileView ? '0 20px' : ''
      }}>
          <div className="col-xs-12 col-sm-4 col-lg-4">
            <InvoiceAdditionalInformation
              classes={classes}
              form={form}
              setValue={setValue}
              control={control}
              disable_quick_pay={disable_quick_pay}
              isGroupInvoice={isGroupInvoice}
            />
          </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '15px',
          justifyContent: mobileView ? 'center' : 'flex-end',
          margin: '40px 10px 15px'
        }}
      >
        <Button
          className={classNames(classes.buttonBack, classes.button)}
          onClick={() => goBack(form.getValues())}
        >
          {t('asiakkaat.back')}
        </Button>
        <Button
          className={classNames(classes.buttonNext, classes.button)}
          type="submit"
          onClick={() => setAttemptingSubmit(true)}
        >
          {t('asiakkaat.next')}
          <span
            style={{
              display: 'inline-block',
              transform: 'scaleX(1.5)',
              fontSize: '18px',
              letterSpacing: '-2px'
            }}
          >
            &nbsp; &gt;
          </span>
        </Button>
      </div>
    </Form>
  )
}

export const InvoiceAdditionalInformation = ({
  classes,
  setValue,
  control,
  disable_quick_pay,
  isGroupInvoice
}) => {
  // KAS-379 new quick pay feature
  let isDisabled = false
  const country = useWatch({
    fields: 'country',
    control: control
  })
  const customer_type = useWatch({
    fields: 'customer_type',
    control: control
  })

  customer_type.customer_type === 'private_person' ||
    parseInt(country.country) !== 1
    ? (isDisabled = true)
    : (isDisabled = false)

  // InvoiceAdditionalInformation is functional component
  // So the setState call need to be wrapped into useEffect
  React.useEffect(() => {
    if (disable_quick_pay || isDisabled) {
      setValue('quick_pay', false)
    }
  }, [disable_quick_pay, isDisabled])

  return (
    <Translation>
      {(t) => {
        return (
          <div
            className="panel panel-default"
            style={{ backgroundColor: 'white', boxShadow: 'none' }}
          >
            <h3
              style={{
                textAlign: !mobileView ? 'left' : 'center',
                fontSize: mobileView && '20px',
                fontWeight: '600',
                color: '#000',
                textTransform: 'none',
                marginBottom: mobileView && '-20px'
              }}
            >
              {t('profile.labelAddDetail')}
            </h3>
            <div style={{ marginTop: '50px' }}>
              <Field
                name="quick_pay"
                label={t('invoice.button3')}
                inputType="checkbox"
                control={control}
                className={classes.checkbox}
                disabled={disable_quick_pay || isDisabled || isGroupInvoice}
              />
              <WatchField control={control} fields={'quick_pay'}>
                {(fields) =>
                  fields.quick_pay === true ? (
                    <div style={{ marginBottom: '8px' }}>
                      {t('invoice.quickPayInfo')}
                    </div>
                  ) : null
                }
              </WatchField>
              <div style={{ display: 'flex' }}>
                <Field
                  name="invoice_price_selection"
                  label={t('invoice.priceSelection')}
                  inputType="checkbox"
                  className={classes.checkbox}
                  control={control}
                />
                <div
                  style={{
                    padding: '5px 0'
                  }}
                >
                  <IconTooltip
                    tooltipShow={t('asiakkaat.tooltip1')}
                    style={{
                      verticalAlign: 'middle'
                    }}
                  />
                </div>
              </div>
              <Field
                name="invoice_reminder"
                label={t('invoice.button4')}
                inputType="checkbox"
                control={control}
                className={classes.checkbox}
              />
              <Field
                name="invoice_lang"
                label={t('invoice.send_invoice_english')}
                inputType="checkbox"
                className={classes.checkbox}
                control={control}
              />
            </div>
          </div>
        )
      }}
    </Translation>
  )
}

export default withStyles(styles)(InvoiceAdditionalInfoForm)
